import { useCallback, useMemo, useState } from 'react';
import adminService from '@wlb/shared/api/admin/adminService';
import type { AdminUser } from '@wlb/shared/api/admin/adminTypes';
import useDebounce from '@wlb/shared/hooks/useDebounce';
import { ToastService } from '@wlb/shared/platform/notifier';
import { getUserFacingErrorMessage } from '@wlb/shared/utils/apiErrorHandler';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { Avatar } from '../components/ui/Avatar';
import { Button } from '../components/ui/Button';
import { DataTable, type DataTableColumn } from '../components/ui/DataTable';
import { Icon } from '../components/ui/Icon';
import { IconButton } from '../components/ui/IconButton';
import { Pagination } from '../components/ui/Pagination';
import { SelectField } from '../components/ui/SelectField';
import { StatusPill } from '../components/ui/StatusPill';
import { TextField } from '../components/ui/TextField';
import { PageHeader } from '../components/layout/PageHeader';
import { AdminAccountDialog } from '../features/adminAccounts/AdminAccountDialog';
import { useTriStateOptions, triStateValue } from '../features/users/triState';
import { useAsyncResource } from '../hooks/useAsyncResource';
import { formatDate } from '../utils/format';
import { resolvePictureUrl } from '../utils/resolveImageUrl';
import '../components/ui/ui.css';
import './pages.css';

const PAGE_SIZE = 20;

export function AdminAccounts() {
  const { t } = useTranslation('admin');
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [pendingGuid, setPendingGuid] = useState<string | null>(null);
  const debouncedSearch = useDebounce(search);
  const triStateOptions = useTriStateOptions();

  const load = useCallback(
    () =>
      adminService.getAdminAccounts({
        searchTerm: debouncedSearch.trim(),
        isActive: triStateValue(activeFilter),
        pageNumber,
        pageSize: PAGE_SIZE,
      }),
    [activeFilter, debouncedSearch, pageNumber],
  );

  const requestKey = `${debouncedSearch}|${activeFilter}|${pageNumber}`;

  const { data, status, refreshing, error, reload } = useAsyncResource(
    load,
    requestKey,
    t('common.errorTitle'),
    true,
    'adminAccounts',
  );

  const toggleActive = useCallback(
    async (account: AdminUser) => {
      setPendingGuid(account.userGuid);
      try {
        await adminService.setAdminAccountActiveStatus(
          account.userGuid,
          !account.isActive,
        );
        ToastService.show({
          type: 'success',
          message: account.isActive
            ? t('adminAccounts.toasts.deactivated')
            : t('adminAccounts.toasts.activated'),
        });
        reload();
      } catch (cause) {
        ToastService.show({
          type: 'error',
          message: getUserFacingErrorMessage(cause, t('common.errorTitle')),
        });
      } finally {
        setPendingGuid(null);
      }
    },
    [reload, t],
  );

  const openEdit = useCallback((account: AdminUser) => {
    setEditing(account);
    setDialogOpen(true);
  }, []);

  const columns = useMemo<readonly DataTableColumn<AdminUser>[]>(
    () => [
      {
        key: 'user',
        header: t('users.columns.user'),
        render: account => (
          <div className="ui-table__cell-with-avatar">
            <Avatar
              src={resolvePictureUrl(account.picture)}
              name={account.firstName || account.userName || t('common.none')}
            />
            <span>
              <span className="ui-table__primary">
                {account.firstName || account.userName || t('common.none')}
              </span>
              {account.userName ? (
                <span className="ui-table__secondary">{account.userName}</span>
              ) : null}
            </span>
          </div>
        ),
      },
      {
        key: 'email',
        header: t('users.columns.email'),
        render: account => (
          <span className="ui-table__truncate">{account.email || t('common.none')}</span>
        ),
      },
      {
        key: 'role',
        header: t('users.columns.role'),
        render: account =>
          account.role ? t(`roles.${account.role}`) : t('roles.unknown'),
      },
      {
        key: 'status',
        header: t('users.columns.status'),
        render: account => (
          <StatusPill
            label={account.isActive ? t('status.active') : t('status.inactive')}
            tone={account.isActive ? 'success' : 'neutral'}
          />
        ),
      },
      {
        key: 'joined',
        header: t('users.columns.joined'),
        render: account => formatDate(account.joiningDate) || t('common.none'),
      },
      {
        key: 'lastActive',
        header: t('users.columns.lastActive'),
        render: account => formatDate(account.lastLoginOn) || t('common.none'),
      },
      {
        key: 'actions',
        header: t('common.actions'),
        render: account => (
          <div className="ui-table__actions">
            <IconButton
              icon="edit"
              tone="indigo"
              label={t('users.actions.edit')}
              onClick={() => openEdit(account)}
            />
            <IconButton
              icon={account.isActive ? 'ban' : 'check'}
              tone={account.isActive ? 'rose' : 'emerald'}
              disabled={pendingGuid === account.userGuid}
              label={
                account.isActive
                  ? t('users.actions.deactivate')
                  : t('users.actions.activate')
              }
              onClick={() => toggleActive(account)}
            />
          </div>
        ),
      },
    ],
    [openEdit, pendingGuid, t, toggleActive],
  );

  return (
    <div className="page page--fill">
      <PageHeader
        title={t('adminAccounts.title')}
        subtitle={t('adminAccounts.subtitle')}
        actions={
          <>
            <IconButton
              icon="refresh"
              tone="slate"
              label={t('common.refresh')}
              disabled={refreshing}
              onClick={reload}
            />
            <Button
              onClick={() => {
                setEditing(null);
                setDialogOpen(true);
              }}>
              <Icon name="plus" size="sm" />
              {t('adminAccounts.create')}
            </Button>
          </>
        }
      />

      <section className="page__toolbar" aria-label={t('common.filters')}>
        <div className="page__toolbar-field page__toolbar-field--wide">
          <TextField
            label={t('common.search')}
            type="search"
            placeholder={t('adminAccounts.searchPlaceholder')}
            value={search}
            onChange={event => {
              setPageNumber(1);
              setSearch(event.target.value);
            }}
          />
        </div>
        <div className="page__toolbar-field">
          <SelectField
            label={t('users.filters.activeLabel')}
            options={triStateOptions}
            value={activeFilter}
            onChange={event => {
              setPageNumber(1);
              setActiveFilter(event.target.value);
            }}
          />
        </div>
      </section>

      <DataTable
        caption={t('adminAccounts.title')}
        columns={columns}
        rows={data?.items ?? []}
        rowKey={account => account.userGuid}
        status={status}
        error={error}
        onRetry={reload}
        emptyTitle={t('adminAccounts.emptyTitle')}
        emptyMessage={t('adminAccounts.emptyMessage')}
        scrollResetKey={requestKey}
        footer={
          data ? (
            <Pagination
              pageNumber={data.pageNumber}
              totalPages={data.totalPages}
              totalCount={data.totalCount}
              hasPreviousPage={data.hasPreviousPage}
              hasNextPage={data.hasNextPage}
              pageSize={PAGE_SIZE}
              onChange={setPageNumber}
            />
          ) : null
        }
      />

      <AdminAccountDialog
        open={dialogOpen}
        account={editing}
        onClose={() => setDialogOpen(false)}
        onSaved={reload}
      />
    </div>
  );
}
